import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import type { IPlanYourTripProps } from 'components/common/PlanYourTrip/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import PartnerBlock from 'components/resort/PartnerBlock/PartnerBlock';
import { useSanitizedHtmlProps } from 'util/sanitized';
import { BlockContent } from 'components/blocks/BlockContent';
import DynamicLodgingForm from 'components/common/PlanYourTrip/Lodging/DynamicLodgingForm';
import DynamicSkiRentalForm from 'components/common/PlanYourTrip/SkiRental/DynamicSkiRentalForm';
import { isSkiRentalsLocale } from 'util/i18nFlags';
import classNames from 'classnames';
import { noOffersContext } from 'page-components/resort/_layout';
import { RESORT_IDS_TO_HIDE_LODGING_FOR } from 'ots-constants';
import { getLocale } from 'i18n/locale';
import { isResortWithoutWeSki } from 'util/isResortWithoutWeSki';
import TripControlBlock from './TripControlBlock';
import WeSki from './WeSki/WeSkiWidget';
import { PlaningViewsEnum } from './constants';

import styles from './styles.module.scss';

export const PlanYourTrip = ({
  title,
  tabDescription,
  redirectLodgingPath,
  initialTab,
  redirectSkiRentalPath,
  hasSkiRental,
  lodgingProvider,
  weSkiDestinationId,
  liftsUrl,
  rentalUrl,
  lessonsUrl,
  onLodgingChange,
  onSkiRentalChange,
  isPartner,
  contextKey,
  resortCountry,
  children,
  templateName,
  isCondensed = false,
  resortId,
}: IPlanYourTripProps) => {
  const { t } = useOTSTranslation('common');
  const [activeView, setActiveView] = useState<PlaningViewsEnum>(initialTab || PlaningViewsEnum.lodging);
  const [shouldHideLodging, setShouldHideLodging] = useState(false);

  const locale = getLocale();
  const shouldHideWeSki = isResortWithoutWeSki(locale, resortId?.toString() || '');

  const onButtonClick = useCallback(
    (value: PlaningViewsEnum) => {
      setActiveView(value);
    },
    [activeView],
  );
  const htmlProps = useSanitizedHtmlProps(tabDescription || '');

  const getPartnerLink = useCallback(() => {
    if (activeView === PlaningViewsEnum.rental && rentalUrl) {
      return rentalUrl;
    }
    if (activeView === PlaningViewsEnum.lessons && lessonsUrl) {
      return lessonsUrl;
    }

    return undefined;
  }, [activeView, rentalUrl, lessonsUrl]);

  const showRentalAsForm = isSkiRentalsLocale() && hasSkiRental && !!redirectSkiRentalPath;

  const noOffers = useContext(noOffersContext);
  // cleanup
  useEffect(() => {
    if (lodgingProvider === 'tripadvisor' && !noOffers) {
      setShouldHideLodging?.(false);
    }
  }, [noOffers]);

  if (shouldHideLodging && lodgingProvider === 'tripadvisor') {
    return null;
  }

  return (
    <BoxWithShadow className={classNames(styles.container, isCondensed && styles.condensed)}>
      {!isCondensed && (
        <>
          <h3 className={styles.heading}>{t('tripWidget.planYourTrip')}</h3>
          <TripControlBlock
            title={title}
            liftsUrl={liftsUrl}
            activeView={activeView}
            rentalUrl={rentalUrl}
            lessonsUrl={lessonsUrl}
            onButtonClick={onButtonClick}
            redirectSkiRentalPath={redirectSkiRentalPath}
            redirectLodgingPath={redirectLodgingPath}
            isPartner={isPartner}
            contextKey={contextKey}
            resortCountry={resortCountry}
            hasSkiRental={hasSkiRental}
            initialTab={initialTab || PlaningViewsEnum.lodging}
            lodgingProvider={lodgingProvider}
            templateName={templateName}
            setShouldHideLodging={setShouldHideLodging}
            resortId={resortId}
          />
        </>
      )}
      {activeView === PlaningViewsEnum.lodging && !RESORT_IDS_TO_HIDE_LODGING_FOR.includes(resortId ?? '') && (
        <>
          {lodgingProvider === 'weski' && weSkiDestinationId && !shouldHideWeSki && (
            <WeSki destinationId={weSkiDestinationId} />
          )}
          {(lodgingProvider === 'tripadvisor' || !lodgingProvider) && (
            <DynamicLodgingForm
              searchRedirectUrl={redirectLodgingPath}
              onChange={onLodgingChange}
            />
          )}
        </>
      )}
      {
        showRentalAsForm && activeView === PlaningViewsEnum.rental && (
          <DynamicSkiRentalForm
            searchRedirectUrl={redirectSkiRentalPath}
            onChange={onSkiRentalChange}
          />
        )
      }
      {
        !showRentalAsForm && activeView === PlaningViewsEnum.rental && (
          <PartnerBlock
            partnerLinkUrl={getPartnerLink()}
            partnerType={activeView}
            title={title}
            contextKey="resort"
          />
        )
      }
      {
        activeView === PlaningViewsEnum.lessons && (
          <PartnerBlock
            partnerLinkUrl={getPartnerLink()}
            partnerType={activeView}
            title={title}
            contextKey="resort"
          />
        )
      }
      {
        activeView === PlaningViewsEnum.flights && tabDescription && (
          <BlockContent>
            <div {...htmlProps} />
          </BlockContent>
        )
      }
      {children}
    </BoxWithShadow>
  );
};
